import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { UserAction } from '../../models/user-action';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgClass } from '@angular/common';
import { BREAKPOINTS } from '@edt/layout';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

export interface UserData {
  username?: string;
  fullname?: string;
  userImage?: string;
}

interface ButtonData {
  type: 'icon' | 'text';
  content: string;
}

@Component({
  selector: 'auth-user-menu',
  standalone: true,
  templateUrl: './user-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkMenuTrigger,
    CdkMenu,
    CdkMenuItem,
    MatIconModule,
    MatButtonModule,
    NgClass,
  ],
})
export class UserMenuComponent {
  readonly loggedInIcon = input<string>();
  readonly loggedInText = input<string>();
  readonly notLoggedInIcon = input<string>();
  readonly notLoggedInText = input<string>();

  readonly userInfo = input<UserData>();
  readonly actions = input<UserAction[]>([]);
  readonly action = output<UserAction>();

  readonly userPanelCssClass = input('py-2 m-2');
  readonly dropContentCssClass = input(
    'bg-[var(--mat-sys-surface-container-lowest)]'
  );
  readonly dropItemCssClass = input('py-3 px-4');

  protected isLoggedIn = computed(() => (this.userInfo() ? true : false));
  protected activeIcon = computed(() =>
    this.isLoggedIn() ? this.loggedInIcon() : this.notLoggedInIcon()
  );
  protected activeText = computed(() =>
    this.isLoggedIn() ? this.loggedInText() : this.notLoggedInText()
  );
  protected buttonData = computed<ButtonData | null>(() => {
    const isMdScreen = this.isMdScreen() ?? false;
    const activeText = this.activeText();
    const activeIcon = this.activeIcon();

    if (!isMdScreen && activeText) {
      return {
        type: 'text',
        content: activeText,
      };
    } else if (!isMdScreen && activeIcon) {
      return {
        type: 'icon',
        content: activeIcon,
      };
    }

    if (isMdScreen && activeIcon) {
      return {
        type: 'icon',
        content: activeIcon,
      };
    } else if (isMdScreen && activeText) {
      return {
        type: 'text',
        content: activeText,
      };
    }

    return null;
  });

  #breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
  protected isMdScreen = toSignal(
    this.#breakpointObserver
      .observe(BREAKPOINTS.sm)
      .pipe(map(result => result.matches))
  );

  onAction(action: UserAction): void {
    this.action.emit(action);
  }
}
